/**
 * Coerce scope sheet values to JSON numbers for PATCH/submit.
 * Kept free of other Project imports to avoid circular deps with interiorScopeApi.
 */

export const PROGRAM_NUMBER_IDS = [
  'bedroom_count',
  'balcony_count',
  'help_room_count',
  'store_room_count',
  'carpet_area_sqft',
  'general_ceiling_height',
  'floor_to_floor_height',
];

/**
 * Explicit answer ids the submit API types as JSON numbers.
 */
export const COMMON_ANSWER_NUMBER_IDS = [
  'length',
  'width',
  'height',
  'ceiling_height',
  'door_height',
  'door_width',
  'door_size',
  'window_count',
  'sill_height',
  'lintel_height',
  'armchairs_qty',
  'wall_lights_qty',
  'ceiling_lights_qty',
  'shelves_qty',
  'drawers_qty',
  'fans_qty',
  'ac_wiring_qty',
  'outdoor_chairs_qty',
  'outdoor_table_qty',
  'lounge_chairs_qty',
  'dining_chairs_qty',
  'dining_table_persons',
  'sofa_seater',
  'washroom_length',
  'washroom_width',
  'washroom_ceiling_height',
  'wardrobe_width',
  'wardrobe_depth',
  'loft_height',
  'railing_height',
  'store_wardrobe_width',
  'store_wardrobe_height',
];

/** Never PATCH these — Stage returns unknown question_id. */
export const NEVER_SEND_ANSWER_IDS = new Set([
  'balcony_door_width',
  'balcony_door_height',
  'corner_unit',
  'corner_unit_qty',
  'tandem_drawers',
  'tandem_drawers_qty',
  'bottle_pullout',
  'bottle_pullout_qty',
  'ac_types',
  'actypes',
  'speakers_required',
]);

/**
 * Never coerce these — API expects strings / choice options.
 * wardrobe_height is a bedroom single-select (see CHOICE_NOT_NUMBER_IDS).
 * store_wardrobe_height remains numeric via COMMON_ANSWER_NUMBER_IDS.
 */
export const STRING_NOT_NUMBER_IDS = new Set([
  'ac_wiring_length',
  'approx_wiring_length',
  'wiring_length',
  'wall_panelling_count',
  'wall_paneling_count',
  'panelling_count',
  'paneling_count',
]);

/** Fields that look numeric but must stay strings for the API. */
export const isStringNotNumberFieldId = (id = '') => {
  const key = String(id || '');
  if (!key) return false;
  if (STRING_NOT_NUMBER_IDS.has(key)) return true;
  if (/wiring_length/i.test(key)) return true;
  if (/cable_length/i.test(key)) return true;
  if (/wall_pann?ell?ing_count/i.test(key)) return true;
  return false;
};

/**
 * Choice fields whose ids end in _height / look numeric but must stay strings.
 * store_wardrobe_height stays numeric (listed in COMMON_ANSWER_NUMBER_IDS).
 */
export const CHOICE_NOT_NUMBER_IDS = new Set(['wardrobe_height']);

/** Single-select answer ids — API expects a string option, never an array. */
export const SINGLE_SELECT_ANSWER_IDS = new Set([
  'hardware_level',
  'wardrobe_height',
]);

/** Normalize "8ft" → "8 ft" so labels match the template contract. */
export const normalizeFtSpacing = (value) => {
  if (value == null) return value;
  return String(value)
    .replace(/(\d)\s*[fF][tT]\b/g, '$1 ft')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Unwrap checkbox-array leftovers and normalize ft spacing for single-selects.
 * Does not validate against template options (sanitizeAnswersForApi does that).
 */
export const normalizeSingleSelectAnswers = (answers = {}) => {
  const out = { ...(answers || {}) };
  Object.keys(out).forEach((key) => {
    const lower = String(key || '').toLowerCase();
    if (
      !SINGLE_SELECT_ANSWER_IDS.has(key) &&
      !SINGLE_SELECT_ANSWER_IDS.has(lower) &&
      lower !== 'hardware' &&
      !lower.endsWith('_hardware_level')
    ) {
      return;
    }
    let v = out[key];
    if (Array.isArray(v)) {
      v = v.find((x) => x != null && String(x).trim() !== '') ?? '';
    }
    if (typeof v === 'number') {
      v = v === 0 ? '' : String(v);
    }
    if (v == null || v === '') {
      delete out[key];
      return;
    }
    if (typeof v === 'object') {
      delete out[key];
      return;
    }
    out[key] = normalizeFtSpacing(v);
  });
  return out;
};

/** True when submit API expects a JSON number for this question id. */
export const isApiNumberFieldId = (id = '') => {
  const key = String(id || '');
  if (!key) return false;
  if (NEVER_SEND_ANSWER_IDS.has(key)) return false;
  if (isStringNotNumberFieldId(key)) return false;
  // Bedroom wardrobe height is a single-select ("Full Height", "8 ft", …).
  if (CHOICE_NOT_NUMBER_IDS.has(key)) return false;
  if (PROGRAM_NUMBER_IDS.includes(key)) return true;
  if (COMMON_ANSWER_NUMBER_IDS.includes(key)) return true;

  if (/_qty$/i.test(key)) return true;
  if (/_seater$/i.test(key)) return true;
  if (/_persons$/i.test(key)) return true;
  if (/_(length|width|height|depth)$/i.test(key)) return true;
  if (/^(length|width|height)$/i.test(key)) return true;
  if (/^(window_count)$/i.test(key)) return true;

  if (
    /^(ceilingHeight|windowCount|sillHeight|lintelHeight|doorHeight|doorWidth|balconyDoorWidth|balconyDoorHeight)$/i.test(
      key
    )
  ) {
    return true;
  }
  return false;
};

/**
 * Coerce UI values to finite JSON numbers for area/room answers.
 *
 * Empty → 1 (not 0): Stage submit treats falsy 0 like missing ("must be a number").
 * Legacy sentinels (-1, 1e-6) are normalized on write.
 */
export const EMPTY_ANSWER_NUMBER = 1;

export const isEmptyAnswerNumber = (value) => {
  if (value === '' || value == null) return true;
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Legacy empty sentinels from earlier FE attempts (not a real zero)
    if (value < 0) return true;
    if (value !== 0 && Math.abs(value) < 1e-3) return true;
  }
  return false;
};

/**
 * Number values for editable form state.
 * - unset / cleared → blank ''
 * - explicit 0 → keep 0 (e.g. window_count)
 * - do not invent defaults
 */
export const toUiNumberValue = (value) => {
  if (value === '' || value == null) return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value < 0) return '';
    if (value !== 0 && Math.abs(value) < 1e-3) return '';
    return value;
  }
  const raw = String(value).trim();
  if (!raw) return '';
  if (raw === '0') return 0;
  const n = Number(raw);
  if (Number.isFinite(n)) {
    if (n < 0) return '';
    if (n !== 0 && Math.abs(n) < 1e-3) return '';
    return n;
  }
  return '';
};

export const coerceToApiNumber = (value, { emptyAs = 0 } = {}) => {
  if (value === '' || value == null) return emptyAs;
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value < 0) return emptyAs;
    if (value === 0) return emptyAs === 0 ? 0 : emptyAs;
    if (Math.abs(value) < 1e-3) return emptyAs;
    return value;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  const raw = String(value).trim();
  if (!raw) return emptyAs;
  const direct = Number(raw);
  if (Number.isFinite(direct)) {
    if (direct < 0) return emptyAs;
    if (direct === 0) return emptyAs === 0 ? 0 : emptyAs;
    if (Math.abs(direct) < 1e-3) return emptyAs;
    return direct;
  }
  const match = raw.match(/^-?\d+(\.\d+)?/);
  if (match) {
    const parsed = Number(match[0]);
    if (Number.isFinite(parsed)) {
      if (parsed < 0) return emptyAs;
      if (parsed === 0) return emptyAs === 0 ? 0 : emptyAs;
      return parsed;
    }
  }
  return emptyAs;
};

/** Area/room answers — empty → 1; explicit 0 is kept (e.g. window_count). */
export const coerceAnswerNumberValue = (value) => {
  // User-entered zero must not be rewritten to 1
  if (value === 0 || value === '0') return 0;
  if (value === '' || value == null) return EMPTY_ANSWER_NUMBER;
  const n = coerceToApiNumber(value, { emptyAs: EMPTY_ANSWER_NUMBER });
  if (n === 0) return 0;
  return n > 0 ? n : EMPTY_ANSWER_NUMBER;
};

/** True when Stage area submit will accept this value as a number. */
export const isValidSubmitNumber = (value) => {
  // Allow explicit 0 (counts); reject empty/NaN/negative/legacy float sentinels.
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return false;
  }
  if (value === 0) return true;
  if (Math.abs(value) < 1e-3) return false;
  return true;
};

/** Ensure wiring-length style fields are JSON strings (API requirement). */
export const coerceToApiString = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

/** Coerce program number fields; always include known program number keys. */
export const coerceProgramNumbers = (program = {}) => {
  const out = { ...(program || {}) };
  Object.keys(out).forEach((key) => {
    if (isApiNumberFieldId(key)) {
      out[key] = coerceToApiNumber(out[key]);
    }
  });
  PROGRAM_NUMBER_IDS.forEach((key) => {
    out[key] = coerceToApiNumber(out[key]);
  });
  return out;
};

/**
 * Program numbers for editable form state — blank instead of default 0,
 * and do not invent missing count keys.
 */
export const coerceProgramNumbersForUi = (program = {}) => {
  const out = { ...(program || {}) };
  Object.keys(out).forEach((key) => {
    if (isApiNumberFieldId(key)) {
      out[key] = toUiNumberValue(out[key]);
    }
  });
  return out;
};

/**
 * Area/room answers for editable form state — blank instead of default 1/0.
 */
export const coerceAnswerNumbersForUi = (answers = {}, optionIds = null) => {
  const out = normalizeSingleSelectAnswers({ ...(answers || {}) });
  const skipOption = (key) => optionIds && optionIds.has(key);
  Object.keys(out).forEach((key) => {
    if (out[key] === null) {
      delete out[key];
      return;
    }
    if (
      SINGLE_SELECT_ANSWER_IDS.has(key) ||
      CHOICE_NOT_NUMBER_IDS.has(key) ||
      isStringNotNumberFieldId(key) ||
      skipOption(key)
    ) {
      return;
    }
    if (!isApiNumberFieldId(key) && !/_qty$/i.test(key) && !/_persons$/i.test(key)) {
      return;
    }
    if (looksLikeOptionLabel(out[key])) return;
    out[key] = toUiNumberValue(out[key]);
  });
  return coerceAnswerStrings(out);
};

/** Fix string-typed fields that may have been stored as numbers. */
export const coerceAnswerStrings = (answers = {}) => {
  const out = { ...(answers || {}) };
  Object.keys(out).forEach((key) => {
    if (isStringNotNumberFieldId(key)) {
      if (typeof out[key] === 'number') {
        out[key] = out[key] === 0 ? '' : coerceToApiString(out[key]);
      } else if (out[key] == null) {
        out[key] = '';
      } else {
        out[key] = coerceToApiString(out[key]);
      }
    }
  });
  return out;
};

/** True when value looks like a choice label, not a numeric input. */
export const looksLikeOptionLabel = (value) => {
  if (typeof value !== 'string') return false;
  const raw = value.trim();
  if (!raw) return false;
  return !Number.isFinite(Number(raw));
};

/**
 * Force every number-like key in answers (and optional known id set) to a JSON number.
 * optionIds: question ids with choice options — never coerce those.
 * Non-numeric strings are preserved (choice labels) even without optionIds.
 */
export const forceAnswerNumbers = (
  answers = {},
  knownIds = null,
  optionIds = null
) => {
  const out = normalizeSingleSelectAnswers({ ...(answers || {}) });
  const skipOption = (key) =>
    (optionIds && optionIds.has(key)) ||
    SINGLE_SELECT_ANSWER_IDS.has(key) ||
    CHOICE_NOT_NUMBER_IDS.has(key);

  const maybeCoerce = (key) => {
    // null / never-send — drop, do not coerce to 1
    if (out[key] === null || NEVER_SEND_ANSWER_IDS.has(key)) {
      delete out[key];
      return;
    }
    if (isStringNotNumberFieldId(key)) return;
    if (
      SINGLE_SELECT_ANSWER_IDS.has(key) ||
      CHOICE_NOT_NUMBER_IDS.has(key)
    ) {
      return;
    }
    if (skipOption(key)) {
      // Dimension fields may be listed in optionMap incorrectly — still number-coerce
      // empty/numeric values so submit does not see "".
      if (
        isApiNumberFieldId(key) &&
        !looksLikeOptionLabel(out[key])
      ) {
        out[key] = coerceAnswerNumberValue(out[key]);
        return;
      }
      if (out[key] === '' || out[key] == null) delete out[key];
      return;
    }
    if (!isApiNumberFieldId(key)) return;
    if (looksLikeOptionLabel(out[key])) return;
    out[key] = coerceAnswerNumberValue(out[key]);
  };

  Object.keys(out).forEach((key) => maybeCoerce(key));
  if (knownIds && knownIds.size > 0) {
    knownIds.forEach((key) => maybeCoerce(key));
  }
  COMMON_ANSWER_NUMBER_IDS.forEach((key) => {
    if (
      Object.prototype.hasOwnProperty.call(out, key) ||
      (knownIds && knownIds.has(key))
    ) {
      maybeCoerce(key);
    }
  });
  return coerceAnswerStrings(out);
};

/** Coerce number-like keys in an answers map (does not drop unknown keys). */
export const coerceAnswerNumbers = (answers = {}, optionIds = null) => {
  const out = normalizeSingleSelectAnswers({ ...(answers || {}) });
  // Keep NEVER_SEND keys in local UI state so inputs remain editable.
  // Write paths must strip via finalizeAnswersForRoomType / stripNeverSendAnswers.
  const skipOption = (key) => optionIds && optionIds.has(key);
  Object.keys(out).forEach((key) => {
    // null = explicit delete marker for invalid/unknown keys
    if (out[key] === null) {
      delete out[key];
      return;
    }
    if (NEVER_SEND_ANSWER_IDS.has(key)) {
      // Still coerce numbers for local display; write path strips these.
      if (isApiNumberFieldId(key) || /_qty$/i.test(key) || /_persons$/i.test(key) || /_(length|width|height)$/i.test(key)) {
        if (!looksLikeOptionLabel(out[key])) {
          out[key] = coerceAnswerNumberValue(out[key]);
        }
      }
      return;
    }
    if (
      SINGLE_SELECT_ANSWER_IDS.has(key) ||
      CHOICE_NOT_NUMBER_IDS.has(key)
    ) {
      return;
    }
    if (isStringNotNumberFieldId(key) || skipOption(key)) return;
    if (!isApiNumberFieldId(key)) return;
    if (looksLikeOptionLabel(out[key])) return;
    out[key] = coerceAnswerNumberValue(out[key]);
  });
  COMMON_ANSWER_NUMBER_IDS.forEach((key) => {
    if (
      SINGLE_SELECT_ANSWER_IDS.has(key) ||
      CHOICE_NOT_NUMBER_IDS.has(key)
    ) {
      return;
    }
    if (isStringNotNumberFieldId(key) || skipOption(key)) return;
    if (!Object.prototype.hasOwnProperty.call(out, key)) return;
    if (out[key] === null) {
      delete out[key];
      return;
    }
    if (looksLikeOptionLabel(out[key])) return;
    out[key] = coerceAnswerNumberValue(out[key]);
  });
  return coerceAnswerStrings(out);
};

/** Deep-coerce program / areas / rooms in a PATCH or expand body. */
export const coerceScopeWriteBody = (body = {}) => {
  if (!body || typeof body !== 'object') return body;
  const next = { ...body };

  if (next.program && typeof next.program === 'object') {
    // Drop guide-sample / non-template program keys that Stage rejects.
    const GUIDE_ONLY_PROGRAM_KEYS = new Set([
      'project_name',
      'total_area',
      'budget',
      'timeline_months',
    ]);
    const cleaned = { ...next.program };
    GUIDE_ONLY_PROGRAM_KEYS.forEach((k) => {
      delete cleaned[k];
    });
    next.program = coerceProgramNumbers(cleaned);
  }

  if (next.areas && typeof next.areas === 'object') {
    const areas = {};
    Object.entries(next.areas).forEach(([roomType, area]) => {
      if (!area || typeof area !== 'object') {
        areas[roomType] = area;
        return;
      }
      // Remap guide aliases at the wire layer too
      const key =
        roomType === 'living_room' || roomType === 'living' || roomType === 'drawing'
          ? 'living_drawing'
          : roomType === 'dining_area'
            ? 'dining'
            : roomType === 'foyer_area'
              ? 'foyer'
              : roomType === 'kitchen_area'
                ? 'kitchen'
                : roomType;
      const prev = areas[key];
      const nextArea = {
        ...(prev || {}),
        ...area,
        room_type: key,
        answers: (() => {
          const coerced = coerceAnswerNumbers({
            ...(prev?.answers || {}),
            ...(area.answers || {}),
          });
          // Wire-level strip — local UI may still hold these keys.
          Object.keys(coerced).forEach((id) => {
            if (NEVER_SEND_ANSWER_IDS.has(id)) delete coerced[id];
          });
          return coerced;
        })(),
      };
      areas[key] = nextArea;
    });
    next.areas = areas;
  }

  if (Array.isArray(next.rooms)) {
    next.rooms = next.rooms
      .map((room) => {
        if (!room || typeof room !== 'object') return null;
        // PATCH rooms: only instance_id / label / answers / notes
        const instance_id = room.instance_id || room.instanceId;
        if (!instance_id) return null;
        return {
          instance_id,
          ...(room.label != null ? { label: room.label } : {}),
          answers: (() => {
            const coerced = coerceAnswerNumbers(room.answers || {});
            Object.keys(coerced).forEach((id) => {
              if (NEVER_SEND_ANSWER_IDS.has(id)) delete coerced[id];
            });
            return coerced;
          })(),
          ...(room.notes ? { notes: room.notes } : {}),
        };
      })
      .filter(Boolean);
    if (!next.rooms.length) delete next.rooms;
  }

  return next;
};

/**
 * Submit API requires typeof === 'number'.
 * Empty string, null, and numeric strings ("0") all fail.
 */
export const isNotJsonNumber = (value) =>
  !(typeof value === 'number' && Number.isFinite(value));

/** @deprecated use isNotJsonNumber — kept for callers */
export const isInvalidStoredNumber = (value) => {
  if (typeof value === 'number') return !Number.isFinite(value);
  return true;
};

/** List program number ids that are not JSON numbers after a round-trip. */
export const listNonNumberProgramFields = (program = {}) =>
  PROGRAM_NUMBER_IDS.filter((key) => isNotJsonNumber(program?.[key]));

/**
 * Scan a raw scope document for number fields that are not JSON numbers.
 * Returns [{ section, question_id, label, got }].
 */
export const collectStrictNumberFailures = (rawScope = {}) => {
  const missing = [];
  const program = rawScope.program || {};
  PROGRAM_NUMBER_IDS.forEach((question_id) => {
    if (isNotJsonNumber(program[question_id])) {
      missing.push({
        section: 'program',
        question_id,
        label: `${question_id} must be a number`,
        got: `${typeof program[question_id]}:${JSON.stringify(program[question_id])}`,
      });
    }
  });
  Object.keys(program).forEach((question_id) => {
    if (PROGRAM_NUMBER_IDS.includes(question_id)) return;
    if (!isApiNumberFieldId(question_id)) return;
    if (isNotJsonNumber(program[question_id])) {
      missing.push({
        section: 'program',
        question_id,
        label: `${question_id} must be a number`,
        got: `${typeof program[question_id]}:${JSON.stringify(program[question_id])}`,
      });
    }
  });

  const areas = rawScope.areas || {};
  Object.entries(areas).forEach(([section, area]) => {
    const answers = area?.answers || {};
    Object.keys(answers).forEach((question_id) => {
      if (!isApiNumberFieldId(question_id)) return;
      const value = answers[question_id];
      // Non-numeric strings are choice options (e.g. bedroom wardrobe_height).
      if (
        typeof value === 'string' &&
        value.trim() &&
        !Number.isFinite(Number(value.trim()))
      ) {
        return;
      }
      if (isNotJsonNumber(value)) {
        missing.push({
          section,
          question_id,
          label: `${question_id} must be a number`,
          got: `${typeof value}:${JSON.stringify(value)}`,
        });
      }
    });
  });

  (Array.isArray(rawScope.rooms) ? rawScope.rooms : []).forEach((room) => {
    const section = room?.room_type || 'room';
    const answers = room?.answers || {};
    Object.keys(answers).forEach((question_id) => {
      if (!isApiNumberFieldId(question_id)) return;
      const value = answers[question_id];
      if (
        typeof value === 'string' &&
        value.trim() &&
        !Number.isFinite(Number(value.trim()))
      ) {
        return;
      }
      if (isNotJsonNumber(value)) {
        missing.push({
          section,
          question_id,
          label: `${question_id} must be a number`,
          got: `${typeof value}:${JSON.stringify(value)}`,
        });
      }
    });
  });

  return missing;
};
